<script setup lang="ts">
import type { RipInstitutionalClaim, PaAPI } from '@rialtic/types'
import { orderInsightsByScore } from '@rialtic/utils'
import { defu } from 'defu'
import {
  getClaimFields,
  type ClaimFieldsSchema,
  type RemoveFieldsPayload,
  type RowSchema,
  type RowSchemaMeta,
} from '../../../../utils/claim-editor'
import {
  splitOtherClaim,
  type ValidationBody,
} from '../../../../utils/claim-splitting'
import { useClaimLab } from '../../../../stores/claimLab'
import type { RawInsight, RawInsights } from '../../../../types'
import type { FormSubmitEvent } from '@nuxt/ui'
import type { ComputedRef, StyleValue } from 'vue'
import ClaimLabPrintStatuses from '../../../../components/ClaimLabPrintStatuses.vue'
import ClaimLabStatusDetails from '../../../../components/ClaimLabStatusDetails.vue'
import ClaimLineEditor from '../../../../components/ClaimLineEditor.vue'
import ClaimHeaderEditor from '../../../../components/ClaimHeaderEditor.vue'
import ClaimInternalFields from '../../../../components/ClaimInternalFields.vue'
import { claimTypeMapShort } from '@rialtic/utils'
import { useClaimLabState } from '../../../../composables/claim-lab'
import { usePrintScreen } from '../../../../composables/print'
import { useHelpers } from '../../../../composables/helpers'
import { $apiFetch } from '../../../../composables/api-fetch'
import {
  provideClaimForm,
  useClaimForm,
  type FieldValue,
} from '../../../../composables/fields'
import { useIsRialticUser } from '../../../../composables/user'
import { useMagicKeys, useScroll, useToggle, whenever } from '@vueuse/core'
import { onMounted, onUnmounted, toRaw, useTemplateRef, watch } from 'vue'
import { useConnector } from '../../../../composables/connector'

definePageMeta({
  layout: 'claim-lab',
  middleware: ['claim-lab'],
  title: 'Claim Lab',
})

useHead({
  bodyAttrs: {
    class: 'overflow-hidden',
  },
})

type PostBody = PaAPI.PostBody['/connector/{connector_id}/claimlab'] & {
  other_claims?: PaAPI.PostBody['/connector/{connector_id}/claimlab']['claim'][]
}

type ClaimResponse = PaAPI.Get['/connector/{connector_id}/claim/{rid}']

const route = useRoute()

const labState = useClaimLabState()

const claimLab = useClaimLab()

const { isPrinting, onPrintScreen } = usePrintScreen()

const userPermissions = inject<ComputedRef<string[]>>('user-permissions')

const { getActiveClaimLine } = useHelpers(userPermissions)

const isRialticUser = useIsRialticUser()

const { connector } = useConnector()

const claimId = computed(() => route.params.claimId as string)

const MAX_NEW_CLAIMLINES = 3

const remainingSlots = computed(
  () =>
    MAX_NEW_CLAIMLINES -
    (claimLab.newClaimLineSequences.size +
      claimLab.otherClaimLineSequence.size),
)

const testResponseError = ref('')

const labContainer = useTemplateRef('labContainer')

const { x } = useScroll(labContainer)

const claimlines = ref<Map<number, RowSchema[]>>(new Map())

const claimHeaderFields = ref<RowSchema[]>([])

const claimInternalFields = ref<RowSchema[]>([])

/* Internal fields hidden from non-Rialtic users still reach the test request */
const hiddenInternalFields = ref<RowSchema[]>([])

const formRows = computed<RowSchema[]>(() => [
  ...[...claimlines.value.values()].flat(),
  ...claimHeaderFields.value,
  ...claimInternalFields.value.flatMap((field) =>
    field.expandItems ? field.expandItems : [field],
  ),
  ...hiddenInternalFields.value,
])

const form = useClaimForm(formRows)

provideClaimForm(form)

const uform = useTemplateRef('uform')

const claimLineSequence = computed(() => {
  let mainSequence = 1
  let otherSequence = 1

  return Object.entries(form.state).reduce(
    (acc, [key, value]) => {
      if (key.startsWith('is_other_claim')) {
        // is_other_claim will always have an appended line number
        const [, parentSequence] = key.split('-')!

        acc[Number(parentSequence)] = value ? otherSequence++ : mainSequence++
      }

      return acc
    },
    {} as Record<number, number>,
  )
})

const claimFieldsSchema = ref<ClaimFieldsSchema>({
  line: [],
  header: [],
  internal: [],
})

const activeSequence = ref(1)

const [isStatusOpen, toggleStatusDetails] = useToggle()

const lineFieldsGridStyle = computed<StyleValue>(() => ({
  gridTemplateRows: `repeat(${claimFieldsSchema.value.line.length}, 34px)`,
}))

const mapField = (field: RowSchema, lineNumber?: number): RowSchema => ({
  ...field,
  name: `${field.editKey || field.name}${lineNumber ? `-${lineNumber}` : ''}`,
})

const processEditableClaimlines = (
  claim: ClaimResponse['claim'],
): Map<number, RowSchema[]> => {
  const result: Map<number, RowSchema[]> = new Map()
  const { lines } = claim.rip

  for (const claimline of lines) {
    const lineFields: RowSchema[] = claimFieldsSchema.value.line.reduce(
      (fields, fieldSchema) => {
        if (
          ['Line', 'Existing Status', 'Test Status'].includes(
            fieldSchema.name as string,
          )
        ) {
          return fields
        }

        const editableField = mapField(fieldSchema, claimline.line_number)

        if (
          fieldSchema.editKey &&
          Object.hasOwn(claimline, fieldSchema.editKey)
        ) {
          editableField.modelValue = claimline[
            fieldSchema.editKey as keyof ClaimResponse['claim']['rip']['lines'][number]
          ] as string | number
        }

        fields.push(editableField)

        return fields
      },
      [] as RowSchema[],
    )

    result.set(claimline.line_number, lineFields)
  }

  return result
}

const processEditableHeaderFields = (
  claim: ClaimResponse['claim'],
): RowSchema[] => {
  const fields: RowSchema[] = []

  for (const fieldSchema of claimFieldsSchema.value.header) {
    const field = mapField(fieldSchema)

    if (typeof field.isHidden === 'function') {
      field.isHidden = field.isHidden(connector.value ?? undefined)
    }

    if (field.name === 'diagnoses') {
      field.modelValue = Array.from(
        { length: 25 },
        // @ts-expect-error - PaAPI types need an update, only professional rip claim is declared for the response type
        (_, i) => claim.rip.header[`health_care_diagnosis_code_${i + 1}`],
      ).reduce((acc, code) => {
        if (typeof code === 'string') {
          return acc.length ? `${acc},${code.trim()}` : code.trim()
        }

        return acc
      }, '')
    } else {
      field.modelValue = fieldSchema.editKey
        ? claim.rip.header[fieldSchema.editKey as keyof typeof claim.rip.header]
        : ''
    }

    fields.push(field)
  }

  return fields
}

const processEditableInternalFields = (
  claim: ClaimResponse['claim'],
): RowSchema[] => {
  const fields: RowSchema[] = []
  const hidden: RowSchema[] = []

  for (const fieldSchema of claimFieldsSchema.value.internal) {
    const field = mapField(fieldSchema) as RowSchemaMeta
    const name = field.editKey || field.name

    if (typeof field.expandItems === 'function') {
      field.expandItems = field.expandItems(claim.rip as RipInstitutionalClaim)
    }

    if (isRialticUser.value) {
      const value = claim.rip.header[name as keyof typeof claim.rip.header]
      field.modelValue = value

      fields.push(field as RowSchema)
    } else if (field.expandItems !== undefined) {
      for (const item of field.expandItems) {
        const value =
          claim.rip.header[item.name as keyof typeof claim.rip.header]

        form.setFieldValue(item.name, value)
        hidden.push({ ...item, isEditable: false })
      }
    } else {
      const value = claim.rip.header[name as keyof typeof claim.rip.header]
      // name will always be defined at this point
      form.setFieldValue(name!, value)
      hidden.push({ ...field, isEditable: false } as RowSchema)
    }
  }

  hiddenInternalFields.value = hidden

  return fields
}

/**
 * Formats and creates RIP formatted data with a validation object
 * @param validBody
 * @returns RIP formatted lines
 */
const formatRipClaim = (validBody: ValidationBody): PostBody['claim'] => {
  const lines = [] as PostBody['claim']['lines']
  const header = {} as PostBody['claim']['header']

  const linesBySequence: Record<string, PostBody['claim']['lines'][number]> = {}

  for (const key in validBody) {
    const sequence = key.match(/-(\d+)$/)?.[1]

    if (sequence) {
      const ripKey = key.replace(/-\d+$/, '')

      if (!linesBySequence[sequence]) {
        linesBySequence[sequence] = {} as PostBody['claim']['lines'][number]
      }

      // @ts-expect-error - all attempts to cast this resulted in `type string is not assignable to type never`
      linesBySequence[sequence][ripKey] = validBody[key] as string
    } else if (key === 'diagnoses') {
      const diagnosisCodes = (validBody[key] as string)
        .split(',')
        .filter(Boolean)

      if (diagnosisCodes?.length) {
        let i = 0

        for (const code of diagnosisCodes) {
          // @ts-expect-error - /claimlab POST body type only defined as RipProfessionalClaim
          header[`health_care_diagnosis_code_${i + 1}`] = code.trim()

          i++
        }
      }
      // Skip undefined or empty string values for any field
    } else if (
      key !== 'include_nonactionable_insight_types' &&
      validBody[key] !== undefined &&
      validBody[key] !== ''
    ) {
      header[key as keyof PostBody['claim']['header']] = validBody[
        key
      ] as string
    }
  }

  // Increment sequence starting at 1 to allow separate sequencing for root and other claim lines
  let sequence = 1

  for (const number in linesBySequence) {
    const line = linesBySequence[number]
    lines.push({
      ...line,
      line_number: sequence++,
    })
  }

  return {
    header,
    lines,
  }
}

const testInProgress = ref(false)

const onSubmit = async (event: FormSubmitEvent<unknown>) => {
  if (testInProgress.value) return
  testInProgress.value = true

  const validBody = event.data as ValidationBody

  const { mainClaimBody, otherClaimBody } = splitOtherClaim(validBody)

  const postBody: PostBody = {
    claim: formatRipClaim(mainClaimBody),
    other_claims: otherClaimBody ? [formatRipClaim(otherClaimBody)] : [],
    claim_id: claimId.value,
    rid: route.params.rid as string,
    ...(claimLab.isAllInsightsEnabled
      ? {
          include_nonactionable_insight_types:
            validBody.include_nonactionable_insight_types as boolean,
        }
      : {}),
  }

  testResponseError.value &&= ''

  try {
    const testData = await $apiFetch<
      PaAPI.Post['/connector/{connector_id}/claimlab']
    >(`/api/claim-lab/connector/${route.params.connectorId}/claimlab`, {
      method: 'POST',
      body: postBody,
    })

    claimLab.setTestResult(
      orderInsightsByScore(testData.insights as any) as RawInsights,
    )

    toggleStatusDetails(true)

    const sequence = getActiveClaimLine(
      testData.insights as unknown as RawInsight[],
    )

    onSelectLine(sequence)
  } catch (error) {
    // TODO: figure out how to not have to hardcode this
    testResponseError.value =
      'The test failed. Please check the data and try again.'
    // TODO: look into a better way than passing undefined here
    claimLab.setTestResult(undefined)
  }

  testInProgress.value = false
}

const handleAddField = (parentFieldName: string) => {
  // Find the parent field in internal or header fields
  const parentField = claimInternalFields.value.find(
    (field) => field.name === parentFieldName,
  )

  if (
    !parentField?.tupleSize ||
    !parentField?.maxEntries ||
    !Array.isArray(parentField?.expandItems)
  ) {
    return
  }

  const { expandItems, tupleSize, maxEntries } = parentField

  const currentCount = Math.floor(expandItems.length / tupleSize)

  // Don't add if we've reached max entries
  if (currentCount >= maxEntries) return

  const nextIndex = currentCount

  const newItems = parentField.getTemplateItem!(nextIndex)

  // Add the new items to expandItems
  parentField.expandItems = [
    ...parentField.expandItems,
    ...(Array.isArray(newItems) ? newItems : [newItems]),
  ]
}

const handleRemoveFields = (payload: RemoveFieldsPayload) => {
  // Find which field collection to update based on source
  const fieldsArray =
    payload.source === 'header'
      ? claimHeaderFields.value
      : claimInternalFields.value

  // Find the parent field that contains expandItems (the grouped field container)
  const parentField = fieldsArray.find(({ name }) => name === payload.metaField)

  if (
    !parentField ||
    !Array.isArray(parentField.expandItems) ||
    !parentField.tupleSize
  ) {
    return
  }

  // Extract the index from the first removed field name (e.g., "value_code_3" -> 3)
  const fieldIndex = payload.keys[0]?.match(/(\d+)$/)?.[1]

  if (!fieldIndex) return

  const removedIndex = parseInt(fieldIndex, 10)
  const tupleSize = parentField.tupleSize

  const maxOriginalIndex = Math.floor(
    parentField.expandItems.length / tupleSize,
  )

  // Build mapping of old values BEFORE clearing anything
  const renumberedFieldValues: Record<string, any> = {}
  let newNumber = 1

  for (let i = 1; i <= maxOriginalIndex; i++) {
    if (i === removedIndex) continue

    // Collect all items in this tuple (e.g., value_code_3 and value_amount_3)
    const tupleItems = parentField.expandItems.filter((item) =>
      item.name?.endsWith(`_${i}`),
    )

    if (tupleItems.length !== tupleSize) continue

    // Get current values from form state BEFORE renumbering
    tupleItems.forEach((item) => {
      const baseNamePattern = item.name!.replace(/_\d+$/, '')
      const newName = `${baseNamePattern}_${newNumber}`
      const oldValue = form.state[item.name!]

      if (oldValue !== undefined) {
        renumberedFieldValues[newName] = oldValue
      }
    })

    newNumber++
  }

  // Clear every previous tuple key; renumbered values are reassigned below
  for (const item of parentField.expandItems) {
    if (item.name) delete form.state[item.name]
  }

  // Build new expandItems array
  const newExpandItems: RowSchema[] = []
  newNumber = 1

  for (let i = 1; i <= maxOriginalIndex; i++) {
    if (i === removedIndex) continue

    const tupleItems = parentField.expandItems.filter((item) =>
      item.name?.endsWith(`_${i}`),
    )

    if (tupleItems.length !== tupleSize) continue

    tupleItems.forEach((item) => {
      const baseNamePattern = item.name!.replace(/_\d+$/, '')
      const newName = `${baseNamePattern}_${newNumber}`
      const newLabel = item.label?.replace(/\d+$/, String(newNumber))

      newExpandItems.push({
        ...item,
        name: newName,
        label: newLabel,
      })
    })

    newNumber++
  }

  // Update the parent field's expandItems
  parentField.expandItems = newExpandItems

  // Finally, set all the renumbered values in form state
  for (const [fieldName, value] of Object.entries(renumberedFieldValues)) {
    form.setFieldValue(fieldName, value)
  }
}

const addClaimline = () => {
  if (
    claimLab.newClaimLineSequences.size >= MAX_NEW_CLAIMLINES ||
    !claimLab.ripData
  ) {
    return
  }

  const keys = Array.from(claimlines.value.keys())
  const lastSequenceNumber = keys[keys.length - 1]!

  const newSequenceNumber =
    claimLab.claimLinesCount === 0 ? 1 : lastSequenceNumber + 1

  const data = structuredClone(toRaw(claimLab.ripData.claim))

  data.rip.lines.push({ line_number: newSequenceNumber })

  const newClaimlines: Map<number, RowSchema[]> =
    processEditableClaimlines(data)

  // we only want to update the new entry
  claimlines.value.set(
    newSequenceNumber,
    newClaimlines.get(newSequenceNumber) as RowSchema[],
  )
  claimLab.addNewClaimLineSequence(newSequenceNumber)
}

const removeClaimline = ({
  sequence,
  isOtherClaim,
}: {
  sequence: number
  isOtherClaim: boolean
}) => {
  const removedFields = claimlines.value.get(sequence) ?? []

  claimlines.value.delete(sequence)

  if (isOtherClaim) claimLab.removeOtherClaimLineSequence(sequence)
  else claimLab.removeNewClaimLineSequence(sequence)

  const entries = Array.from(claimlines.value.entries())

  /* Form values are keyed by field name, so they follow their line to its new number */
  const renumberedValues: Record<string, FieldValue> = {}

  entries.forEach(([oldSequence, fields], index) => {
    const names = [
      ...fields.map(({ name }) => name),
      `is_other_claim-${oldSequence}`,
    ]

    names.forEach((name) => {
      renumberedValues[name.replace(/-\d+$/, `-${index + 1}`)] =
        form.state[name]
    })
  })

  for (const name of [
    ...removedFields.map((field) => field.name),
    `is_other_claim-${sequence}`,
    ...entries.flatMap(([oldSequence, fields]) => [
      ...fields.map((field) => field.name),
      `is_other_claim-${oldSequence}`,
    ]),
  ]) {
    delete form.state[name]
  }

  Object.assign(form.state, renumberedValues)

  claimlines.value.clear()

  entries.forEach(([_oldSequence, fields], index) => {
    const newSequence = index + 1

    const updatedFields = fields.map((field) => ({
      ...field,
      name: field.name.replace(/-\d+$/, `-${newSequence}`),
    }))

    claimlines.value.set(newSequence, updatedFields)
  })

  if (claimLab.newClaimLineSequences.size > 0) {
    const filteredSequences = Array.from(claimLab.newClaimLineSequences).filter(
      (seq) => seq !== sequence,
    )

    const newSequences: number[] = filteredSequences.map((_seq, index) => {
      const totalLines = entries.length

      return totalLines - filteredSequences.length + index + 1
    })

    claimLab.newClaimLineSequences.clear()
    newSequences.forEach((seq) => claimLab.addNewClaimLineSequence(seq))
  }

  if (claimLab.otherClaimLineSequence.size > 0) {
    const filteredSequences = Array.from(
      claimLab.otherClaimLineSequence,
    ).filter((seq) => seq !== sequence)

    const newSequences: number[] = filteredSequences.map((_seq, index) => {
      const totalLines = entries.length

      return totalLines - filteredSequences.length + index + 1
    })

    claimLab.otherClaimLineSequence.clear()
    newSequences.forEach((seq) => claimLab.addOtherClaimLineSequence(seq))
  }
}

const toggleAllInsights = (include: boolean) => {
  form.setFieldValue('include_nonactionable_insight_types', include)
}

const scrollToLine = (lineNumber: number) => {
  if (!lineNumber || lineNumber <= 1) {
    x.value = 0

    return
  }

  x.value = (lineNumber - 1) * 164
}

const onSelectLine = (lineNumber: number) => {
  activeSequence.value = lineNumber
  scrollToLine(lineNumber)
}

const { escape: esc, meta_enter } = useMagicKeys()

whenever(esc as ComputedRef<boolean>, () => {
  isStatusOpen.value = false
})

whenever(meta_enter as ComputedRef<boolean>, () => {
  uform.value?.submit()
})

watch([() => claimLab.ripData, connector], () => {
  if (!claimLab.ripData || !connector.value) return

  claimFieldsSchema.value = getClaimFields(claimLab.claimType)
  claimlines.value = processEditableClaimlines(claimLab.ripData.claim)
  claimHeaderFields.value = processEditableHeaderFields(claimLab.ripData.claim)
  claimInternalFields.value = processEditableInternalFields(
    claimLab.ripData.claim,
  )
  labState.value = defu(
    { connectorName: claimLab.ripData.connector?.name },
    labState.value,
  )

  if (claimLab.isAllInsightsEnabled) {
    form.setFieldValue('include_nonactionable_insight_types', false)
  }
})

onMounted(() => {
  claimLab.fetchClaimData().catch(() => {
    useToast().add({ title: 'Unable to fetch claim data', color: 'error' })
  })
})

onUnmounted(() => {
  claimLab.resetClaimLab()
})
</script>

<template>
  <UForm
    ref="uform"
    class="h-full"
    :schema="form.schema.value"
    :state="form.state"
    @submit="onSubmit"
  >
    <main class="grid h-full w-full grid-rows-[auto_1fr] print:h-auto">
      <header
        class="relative z-0 flex items-center justify-between border-b border-neutral-200 px-2 py-1 sm:px-4 lg:px-7 lg:py-4"
      >
        <div>
          <h1
            class="text-h2 font-semibold text-neutral-900 lg:mb-1 lg:text-lg lg:leading-9"
          >
            Claim Lab
          </h1>
          <p
            class="text-secondary-700 text-button-1 flex items-center gap-1 text-xs lg:text-base"
          >
            <Protected>
              {{ claimId }}
            </Protected>
            <template v-if="claimLab.claimType">
              <span class="text-size-sm text-neutral-900">|</span>
              {{ claimTypeMapShort[claimLab.claimType] }}
            </template>
          </p>
        </div>

        <div class="flex items-center space-x-2 print:hidden">
          <div v-if="testResponseError" class="text-error text-xs">
            {{ testResponseError }}
          </div>

          <UTooltip
            v-if="claimLab.isAllInsightsEnabled"
            arrow
            text="When selected test responses will include non-actionable insights."
          >
            <label class="flex items-center gap-1">
              <span class="text-sm">All Insights</span>
              <USwitch
                :model-value="
                  form.state.include_nonactionable_insight_types === true
                "
                color="secondary"
                @update:model-value="toggleAllInsights"
              />
            </label>
          </UTooltip>

          <UButton
            :loading="testInProgress"
            color="secondary"
            size="sm"
            trailing-icon="i-ph-test-tube"
            type="submit"
          >
            <span class="hidden sm:inline">Run test</span>
          </UButton>

          <div class="relative">
            <UButton
              :disabled="
                claimLab.newClaimLineSequences.size +
                  claimLab.otherClaimLineSequence.size >=
                MAX_NEW_CLAIMLINES
              "
              color="neutral"
              size="sm"
              trailing-icon="i-ph-plus"
              variant="outline"
              @click="addClaimline"
            >
              <span class="hidden sm:inline">Add claim line</span>
            </UButton>
            <UBadge
              class="absolute -right-2 -top-4 text-white ring-0"
              :class="
                remainingSlots === 0 ? 'bg-warning-300' : 'bg-success-400'
              "
              :label="`${remainingSlots}/${MAX_NEW_CLAIMLINES}`"
              size="sm"
              variant="solid"
            />
          </div>

          <UButton
            color="neutral"
            size="sm"
            trailing-icon="i-ph-file-pdf"
            variant="outline"
            @click="onPrintScreen()"
          >
            <span class="hidden sm:inline">Export details to PDF</span>
          </UButton>
        </div>
      </header>
      <section
        v-if="claimlines.size"
        class="grid grid-cols-[1fr_auto] grid-rows-[100%] overflow-hidden print:block print:h-auto print:overflow-visible"
      >
        <div
          ref="labContainer"
          class="snap-x scroll-pl-[220px] overflow-auto scroll-smooth"
        >
          <div
            class="grid min-w-max grid-flow-col grid-cols-[220px_auto] print:h-auto print:overflow-visible"
          >
            <div
              class="z-1 border-default sticky left-0 row-span-full grid grid-flow-col border-r"
              :style="lineFieldsGridStyle"
            >
              <div
                v-for="({ label, isRequired }, index) of claimlines.get(1)"
                :key="label"
                class="text-default flex h-full w-full items-center border-b border-b-neutral-200 bg-white p-2 text-xs last:border-b-0 sm:px-4 lg:px-7"
                :class="{ 'sticky top-0': index === 0 }"
              >
                {{ label }}
                <span v-if="isRequired" class="text-red-700">*</span>
              </div>
            </div>
            <div
              class="z-0 grid grid-flow-col grid-cols-[repeat(auto-fit,164px)] *:min-w-[164px] print:grid-flow-row"
              :style="lineFieldsGridStyle"
            >
              <ClaimLineEditor
                v-for="([key, value], index) in claimlines"
                :key="key"
                class="snap-start"
                :claim="claimLab.fhirData"
                :claim-line-sequence="claimLineSequence"
                :fields="value"
                :sequence="key"
                :sequential-index="index + 1"
                @remove-claimline="removeClaimline"
              />
            </div>
          </div>

          <!-- 1204px max-width = row heading width + (line field width * 6) -->
          <section class="sticky left-0 max-w-[1204px]">
            <ClaimHeaderEditor
              :fhir-claim="claimLab.fhirData"
              :fields="claimHeaderFields"
            />

            <ClaimInternalFields
              v-if="isRialticUser"
              :fields="claimInternalFields"
              @add-field="handleAddField"
              @remove-fields="handleRemoveFields"
            />
          </section>
        </div>

        <ClaimLabStatusDetails
          v-model:open="isStatusOpen"
          :active-sequence="activeSequence"
          :all-insights="
            claimLab.isAllInsightsEnabled &&
            form.state.include_nonactionable_insight_types === true
          "
          :claim-line-sequence="claimLineSequence"
          :fhir-claim-lines="claimLab.fhirClaimLines"
          :test-in-progress="testInProgress"
          @select-line="onSelectLine"
          @submit-form="uform?.submit()"
        />
      </section>

      <ClaimLabPrintStatuses
        v-if="isPrinting"
        v-bind="{ fhirClaimLines: claimLab.fhirClaimLines, activeSequence }"
      />
    </main>
  </UForm>
</template>
